// /en/civil-registration — Wave 77: EN hub of civil-registration document families
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { CIVIL_REG_GROUPS, CIVILREG_APOSTILLE_EN, CIVILREG_AUTHORITY_EN } from "@/data/civil-registration-groups";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/en/civil-registration";
const URL = `${SITE}${PATH}`;
const TH_URL = `${SITE}/civil-registration`;
const TITLE = "Thai Civil Registration Documents — 6 Cases | NYC";
const DESC =
  "Thai civil registration paperwork in six cases: birth certificates, house registration, ID cards, name-change certificates, death certificates and status certificates for use abroad.";

export const Route = createFileRoute("/en/civil-registration/")({
  loader: async () => {
    const { CIVIL_REG_GROUPS } = await import("@/data/civil-registration-groups");
    return {
      groups: CIVIL_REG_GROUPS.map((g) => ({
        slug: g.slug,
        title: g.titleEn,
        faqs: g.faqsEn.map((f) => ({ q: f.q, a: f.a })),
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "en") },
      { name: "description", content: ctaDesc(DESC, PATH, "en") },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "en") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "en") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: TH_URL },
      { rel: "alternate", hrefLang: "x-default", href: TH_URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "en",
          mainEntity: (loaderData?.groups ?? []).flatMap((g) =>
            g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "Thai civil registration document cases",
          numberOfItems: (loaderData?.groups ?? []).length,
          itemListElement: (loaderData?.groups ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `${URL}/${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: CivilRegEnPage,
});

function CivilRegEnPage() {
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">Civil registration / all cases</nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Thai civil registration documents — 6 cases</h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        Civil registration records are issued by district registrars under the Civil Registration Act, so the office that
        issued the record decides which certified copy you can obtain and which legalisation chain applies before the
        document works abroad. Each case below lists the documents to prepare, the step order, a pre-filing checklist,
        practical tips and the common pitfalls.
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">{CIVILREG_AUTHORITY_EN}</aside>
      <aside className="mt-3 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">{CIVILREG_APOSTILLE_EN}</aside>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {CIVIL_REG_GROUPS.map((g) => (
          <li key={g.slug}>
            <Link to="/en/civil-registration/$group" params={{ group: g.slug }} className="text-primary hover:underline">
              {g.titleEn}
            </Link>
          </li>
        ))}
      </ul>

      {CIVIL_REG_GROUPS.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-12 scroll-mt-24">
          <h2 className="text-2xl font-semibold">{g.titleEn}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryEn}</p>
          <div className="mt-4 space-y-4 leading-8">
            {g.bodyEn.slice(0, 2).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <Link
            to="/en/civil-registration/$group"
            params={{ group: g.slug }}
            className="mt-3 inline-block text-primary hover:underline"
          >
            Read the full guide: {g.titleEn} →
          </Link>
        </section>
      ))}

      <p className="mt-10 rounded-lg border border-border bg-muted/30 p-4 text-sm leading-7">
        Send scans via LINE @nycli, call +66-83-249-4999 or email nycli@ilc.ltd. Our team will confirm the steps, any
        missing documents and a realistic timeline for your case.
      </p>
          <ServiceAuthorityBlock topic="civil" locale="en" />
    </main>
  );
}
